import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import api from "./api";

// Split a long response into natural speech chunks (never mid-word), so long
// answers are spoken in full and playback can start quickly / be interrupted.
const MAX_CHUNK = 320;
export function chunkText(text) {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (!clean) return [];
  if (clean.length <= MAX_CHUNK) return [clean];
  const sentences = clean.match(/[^.!?]+[.!?]+["')\]]*\s*|[^.!?]+$/g) || [clean];
  const chunks = [];
  let cur = "";
  for (const s of sentences) {
    if (s.length > MAX_CHUNK) {
      if (cur.trim()) { chunks.push(cur.trim()); cur = ""; }
      const words = s.split(" ");
      let line = "";
      for (const w of words) {
        if ((line + " " + w).trim().length > MAX_CHUNK && line) { chunks.push(line.trim()); line = ""; }
        line += (line ? " " : "") + w;
      }
      if (line.trim()) cur = line;
      continue;
    }
    if ((cur + s).length > MAX_CHUNK && cur) { chunks.push(cur.trim()); cur = ""; }
    cur += s;
  }
  if (cur.trim()) chunks.push(cur.trim());
  return chunks.filter(Boolean);
}

// Centralized TTS queue: one audio at a time, sequential chunk playback,
// one retry per chunk, autoplay-block detection, cancellation on interrupt.
export function useTTS() {
  const audioRef = useRef(null);
  const tokenRef = useRef(0);
  const pendingRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [needsUnlock, setNeedsUnlock] = useState(false);
  const [statusById, setStatusById] = useState({});

  const setStatus = (id, s) => setStatusById((p) => ({ ...p, [id]: s }));

  const cancel = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
  };

  const stop = useCallback(() => {
    tokenRef.current++;
    cancel();
    setIsSpeaking(false);
    setActiveId(null);
  }, []);

  const playUrl = (url) => new Promise((resolve, reject) => {
    const a = new Audio(url);
    audioRef.current = a;
    a.onended = () => resolve();
    a.onerror = () => reject(new Error("audio_error"));
    const p = a.play();
    if (p && p.catch) p.catch((err) => reject(err));
    console.log("[TTS] Playback started");
  });

  const run = useCallback(async (id, text, opts, myToken) => {
    console.log("[TTS] Request started", id);
    setActiveId(id); setIsSpeaking(true); setStatus(id, "generating");
    const chunks = chunkText(text);
    try {
      for (let i = 0; i < chunks.length; i++) {
        if (tokenRef.current !== myToken) { console.log("[TTS] cancelled"); return; }
        let audioUrl = null;
        for (let attempt = 0; attempt < 2 && !audioUrl; attempt++) {
          try {
            const { data } = await api.post("/tts", { text: chunks[i], assistant: opts.assistant, voice_id: opts.voiceId });
            audioUrl = data.audio;
            console.log("[TTS] Request succeeded", `chunk ${i + 1}/${chunks.length}`);
          } catch (e) {
            const code = e?.response?.status;
            console.warn("[TTS] Request failed", `chunk ${i}`, "attempt", attempt, "status", code);
            if (code === 402) {  // terminal: ElevenLabs credits exhausted — don't retry
              setStatus(id, "failed");
              toast.error("Voice is temporarily unavailable — the ElevenLabs voice credits have run out. You can still read the reply here.");
              if (tokenRef.current === myToken) { setIsSpeaking(false); setActiveId(null); }
              return;
            }
            if (attempt === 1) throw e;
          }
        }
        if (tokenRef.current !== myToken) return;
        console.log("[TTS] Audio created", `chunk ${i + 1}`);
        setStatus(id, "playing");
        try {
          await playUrl(audioUrl);
          console.log("[TTS] Playback ended", `chunk ${i + 1}`);
        } catch (err) {
          const blocked = err?.name === "NotAllowedError" || String(err?.message || err).includes("NotAllowed") || String(err).includes("gesture");
          if (blocked) {
            console.warn("[TTS] Playback blocked — needs user gesture to enable voice");
            pendingRef.current = { id, text, opts };
            setNeedsUnlock(true);
            setStatus(id, "pending");
            setIsSpeaking(false); setActiveId(null);
            return;
          }
          throw err;
        }
      }
      if (tokenRef.current === myToken) { setStatus(id, "completed"); console.log("[TTS] completed", id); }
    } catch (e) {
      console.error("[TTS] failed", id, e?.response?.status || e?.message);
      setStatus(id, "failed");
    } finally {
      if (tokenRef.current === myToken) { setIsSpeaking(false); setActiveId(null); audioRef.current = null; }
    }
  }, []);

  const speak = useCallback((id, text, opts = {}) => {
    console.log("[TTS] Response received → queueing", id);
    tokenRef.current++;
    cancel();
    setNeedsUnlock(false);
    run(id, text, opts, tokenRef.current);
  }, [run]);

  const enableVoice = useCallback(() => {
    setNeedsUnlock(false);
    const p = pendingRef.current;
    pendingRef.current = null;
    if (p) speak(p.id, p.text, p.opts);
  }, [speak]);

  return { speak, stop, enableVoice, isSpeaking, activeId, needsUnlock, statusById };
}
